import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Fab from "@mui/material/Fab";

import ImportExportIcon from "@mui/icons-material/ImportExport";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";

import { useTranslation } from "react-i18next";
import useLocalMoodboard from "../useLocalMoodboard";
import ExportDialog from "./ExportDialog";
import ImportDialog from "./ImportDialog";
import usePublicMoodboards from "./usePublicMoodboards";
import RemoveBoardDialog from "./RemoveBoardDialog";
import ClearDialog from "./ClearDialog";
import useSettings from "../../User/useSettings";
import useClasses from "../../useClasses";

const styles = (theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  fab: {
    position: "fixed",
    right: theme.spacing(2),
    bottom: theme.spacing(10),
  },
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
});

export default function ImportExport() {
  const classes = useClasses(styles);
  const { t } = useTranslation();
  const [importedBoard, setImportedBoard] = useState({
    id: "local",
    name: "None",
  });
  const { settings } = useSettings();
  const { localMoodboard } = useLocalMoodboard(); // local moodboard of client
  const { publicMoodboards, refetchPublicMoodboards } = usePublicMoodboards(); // all saved moodboards in lib
  // local state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [importDialog, setImportDialog] = useState({
    open: false,
  });
  const [exportDialog, setExportDialog] = useState({
    open: false,
    mode: "export",
  });
  const [removeDialog, setRemoveDialog] = useState({
    open: false,
  });
  const [clearDialog, setClearDialog] = useState({
    open: false,
  });

  // constructor
  useEffect(() => {
    if (settings?.moodBoardId) {
      const moodboard = publicMoodboards.find(
        (board) => board.id === settings.moodBoardId
      );
      if (moodboard) {
        setImportedBoard(moodboard);
      }
    }
  }, [settings, publicMoodboards]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const options = [
    {
      id: "import",
      title: "Import from library",
      function: () => {
        refetchPublicMoodboards(); // update the moodboards list
        setImportDialog({ open: true });
      },
      visible: publicMoodboards.length > 0, // import if there are more than 1 public moodboards
      icon: GetAppOutlinedIcon,
    },
    {
      id: "export",
      title: "Export to library",
      function: () => setExportDialog({ open: true, mode: "export" }),
      visible: true,
      icon: PublishOutlinedIcon,
    },
    {
      id: "save",
      title: `Save "${importedBoard.name}"`,
      function: () => setExportDialog({ open: true, mode: "save" }),
      visible: importedBoard.id !== "local", // local boards can not be saved
      icon: SaveOutlinedIcon,
    },
    {
      id: "remove",
      title: `Remove "${importedBoard.name}"`,
      function: () => setRemoveDialog({ open: true }),
      visible: importedBoard.id !== "local", // local boards can not be removed
      icon: DeleteForeverOutlinedIcon,
    },
    {
      id: "empty",
      title: "Clear all",
      function: () => setClearDialog({ open: true }),
      visible: true,
      icon: ClearOutlinedIcon,
    },
  ];

  const handleOptionClick = (option) => {
    option.function();
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Fab
        variant="extended"
        className={classes.fab}
        aria-controls="import-export-menu"
        aria-haspopup="true"
        onClick={handleOpen}
      >
        <ImportExportIcon className={classes.extendedIcon} />
        Import/Export graphs
      </Fab>
      <Menu
        id="import-export-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock={true}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        {options.map((option) =>
          option.visible ? (
            <MenuItem key={option.id} onClick={() => handleOptionClick(option)}>
              <ListItemIcon>
                <option.icon fontSize="small" />
              </ListItemIcon>
              <Typography variant="inherit">{option.title}</Typography>
            </MenuItem>
          ) : null
        )}
      </Menu>
      <ImportDialog
        importDialog={importDialog}
        setImportDialog={setImportDialog}
        setImportedBoard={setImportedBoard}
      />
      <ExportDialog
        exportDialog={exportDialog}
        setExportDialog={setExportDialog}
        board={importedBoard}
        setImportedBoard={setImportedBoard}
      />
      <RemoveBoardDialog
        removeDialog={removeDialog}
        setRemoveDialog={setRemoveDialog}
        board={importedBoard}
        setImportedBoard={setImportedBoard}
      />
      <ClearDialog clearDialog={clearDialog} setClearDialog={setClearDialog} />
    </div>
  );
}
